/**
 * Poll a background job (GET /api/jobs/:id) until it finishes.
 *
 * The heavy AI endpoints enqueue a job and answer 202 `{ jobId }`. Callers poll
 * this helper to get the eventual result. Resolves with the job's `result` on
 * success, throws on failure or timeout.
 *
 * Ported from artifacts/mobile/lib/pollJob.ts — same contract, `authFetch`
 * instead of a module-scope token read. Jobs enqueued with an `ownerId` (the
 * scan job is) require the caller's JWT to pass the per-owner authorization
 * check on GET /api/jobs/:id, which is what authFetch attaches.
 */
import type { AuthFetch } from "./importHub";

export interface JobStatus<T> {
  id: string;
  type: string;
  status: "queued" | "processing" | "done" | "failed";
  result: T | null;
  error: string | null;
}

interface PollOptions {
  intervalMs?: number;
  maxAttempts?: number;
}

/**
 * The error thrown when THIS POLL's attempt ceiling runs out while the job is
 * still queued or processing — as opposed to the job coming back `failed`.
 *
 * The two are opposite situations wearing the same `Error` costume, and the
 * scan flow needs to tell them apart: a timeout is RESUMABLE — poll the same
 * jobId again and no new work is enqueued, no second nondeterministic answer is
 * bought — while a failed job is terminal and only a NEW scan could follow it.
 * The marker rides on the error rather than on its message because COPY IS NOT
 * A PROTOCOL: reworded text must never silently reclassify a job as
 * unrecoverable, which is what matching /Timed out/ would have done.
 */
export interface JobPollTimeoutError extends Error {
  timedOut: true;
  /**
   * Whether ANY attempt in this poll actually read the job back.
   *
   * `if (!res.ok) continue` treats every non-200 — a 401 on an expired token, a
   * 404 on a job this client does not own or that no longer exists — exactly
   * like "still processing", so the loop exhausts and throws the timeout above.
   * The two are opposite facts: one job is slow, the other is unreadable and
   * will still be unreadable in a minute. Without this, the scan flow offered
   * "Keep waiting" forever on a job it could never see (PR #848 review, F3).
   *
   * Recorded here rather than fixed by changing the `continue`, which other
   * callers depend on for transient network blips, and CLASSIFIED in scan.ts —
   * this module reports what happened, it does not decide what it means.
   */
  sawReadableResponse: boolean;
}

export async function pollJobResult<T>(
  authFetch: AuthFetch,
  jobId: string,
  opts: PollOptions = {},
): Promise<T> {
  const intervalMs = opts.intervalMs ?? 1200;
  const maxAttempts = opts.maxAttempts ?? 50;

  // Set the FIRST time a response actually parses into a job record. See
  // JobPollTimeoutError.sawReadableResponse — the `continue` below is kept
  // exactly as it was, and this only records what it swallowed.
  let sawReadableResponse = false;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    let res: Response;
    try {
      res = await authFetch(`/api/jobs/${jobId}`);
    } catch {
      continue;
    }
    if (!res.ok) continue;
    const data = (await res.json()) as JobStatus<T>;
    sawReadableResponse = true;
    if (data.status === "done") {
      if (data.result == null) throw new Error("Job completed without a result");
      return data.result;
    }
    if (data.status === "failed") {
      throw new Error(data.error ?? "Job failed");
    }
  }

  const timeout = new Error("Timed out waiting for job to complete") as JobPollTimeoutError;
  timeout.timedOut = true;
  timeout.sawReadableResponse = sawReadableResponse;
  throw timeout;
}
